import { useEffect } from "react";
import api from "@/lib/api";
import { useAltivMutation } from "@/hooks/useAltivMutation";

/**
 * Wires the "Silenciar" push action button to the backend mute endpoint.
 *
 * Two entry points, both end up calling POST /api/notifications/mute:
 *   1. SW postMessage: when the user taps "Silenciar" with the app open in a
 *      tab, the SW relays the click here so we reuse the foreground JWT.
 *   2. Query param `?mute_delayed=<leg_id>`: when the app wasn't open, the
 *      SW falls back to opening /flights with this query — we read it on
 *      mount, mute, then strip the query.
 *
 * Why not authenticate from the SW: the JWT lives in localStorage, which
 * the SW can't reach. Sending tokens through IDB is doable but increases
 * surface area; for a single click action, foreground relay is cheaper.
 */
export default function MuteDelayedHandler() {
  // Fire-and-forget mutation: there's no UI to spin or block on — the click
  // came from a push notification, the user has already moved on. We still
  // route through useAltivMutation so errors land in Sentry + admin_alerts
  // instead of being silently swallowed.
  const muteMutation = useAltivMutation({
    mutationFn: ({ legId, eventType }) =>
      api.post("/notifications/mute", { leg_id: legId, event_type: eventType }),
    invalidates: ["/notifications"],
  });

  useEffect(() => {
    const muteLeg = (legId, eventType = "DELAYED") => {
      if (!legId) return Promise.resolve();
      return muteMutation.mutateAsync({ legId, eventType }).catch(() => {
        // Swallow — reporter already captured it. We return a settled promise
        // so the query-strip step below still runs on the fallback path.
      });
    };

    // 1) postMessage relay from the SW.
    const onMessage = (event) => {
      const data = event.data;
      if (data && data.kind === "mute_request" && data.leg_id) {
        muteLeg(data.leg_id, data.event_type);
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onMessage);
    }

    // 2) Query param fallback (SW had no open client).
    try {
      const params = new URLSearchParams(window.location.search);
      const legId = params.get("mute_delayed");
      if (legId) {
        muteLeg(legId, "DELAYED").finally(() => {
          // Strip the query so a refresh doesn't re-trigger the mute.
          params.delete("mute_delayed");
          const search = params.toString();
          const newUrl =
            window.location.pathname + (search ? `?${search}` : "") + window.location.hash;
          window.history.replaceState({}, "", newUrl);
        });
      }
    } catch {}

    return () => {
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", onMessage);
      }
    };
    // Mount-only: registramos un listener global del service worker una vez.
    // Sin deps porque cualquier valor que lea adentro (api, ubicacion) o es
    // estable o se lee fresh via window/navigator/api singleton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
